import { checkHandleFormat, handleTarget } from '@guildite/domain/handles';

import type { PersonResult, ProfileLookup, ProfilesPort, PublicProfile } from './ports.ts';
import type { WorkerClient } from './worker.ts';

const UID = /^[A-Za-z0-9_-]{1,128}$/;

export function toPublicProfile(uid: string, record: Record<string, unknown> | null): PublicProfile | null {
  if (!record) return null;
  const photo = String(record.photoUrl || '');
  return {
    uid,
    handle: typeof record.handle === 'string' ? record.handle : null,
    name: String(record.name || '').replace(/\s+/g, ' ').trim().slice(0, 60) || 'Guildite member',
    photoUrl: photo.startsWith('https://') ? photo : null,
    bannerUrl: String(record.bannerUrl || '').startsWith('https://') ? String(record.bannerUrl) : null,
    bio: String(record.bio || '').slice(0, 300),
    badges: Array.isArray(record.badges) ? record.badges.filter((badge): badge is string => badge === 'official' || badge === 'staff') : [],
  };
}

export function createProfiles({ worker, readProfile, readHandleRecord, now = () => Date.now() }: {
  worker: WorkerClient;
  readProfile: (uid: string) => Promise<Record<string, unknown> | null>;
  readHandleRecord: (handle: string) => Promise<Record<string, unknown> | null>;
  now?: () => number;
}): ProfilesPort {
  async function byUid(uid: string, requested: string): Promise<ProfileLookup> {
    const profile = toPublicProfile(uid, await readProfile(uid));
    if (!profile) return { status: 'missing' };
    return { status: 'found', profile, redirectTo: profile.handle && profile.handle !== requested ? profile.handle : null };
  }

  return {
    getMine: () => worker.call('get_my_profile'),
    findPeople: async query => (await worker.call<{ people: PersonResult[] }>('find_people', { query })).people,
    setUsername: async (handle, referredByHandle) => (await worker.call<{ profile: PublicProfile }>('set_username', { handle, ...(referredByHandle ? { referredByHandle } : {}) })).profile,
    update: async patch => (await worker.call<{ profile: PublicProfile }>('update_profile', patch)).profile,
    async lookup(value) {
      const requested = value.trim();
      const format = checkHandleFormat(requested);
      if (format.ok) {
        const target = handleTarget(await readHandleRecord(format.handle), now());
        if (target?.type === 'user') return byUid(target.id, requested);
      }
      return UID.test(requested) ? byUid(requested, requested) : { status: 'missing' };
    },
  };
}
